<script setup lang="ts">
import { AddPlusIcon, ButtonComponent, Card, FlexComponent, InputComponent, NoteComponent } from '@metapic-apps/shared'
import { ElForm, ElFormItem, ElIcon, ElNotification, type FormInstance, type FormItemRule } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import { useUpdateAdvertiser } from '@/api/advertiser.api'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import { formatDomain } from '@/lib/helpers/domain.helpers'

type Domain = {
  id: number
  text: string
}

const props = defineProps<{
  advertiserId: string
  domains: Domain[]
}>()

const form = reactive<{ domains: Domain[] }>({ domains: [] })
const formRef = ref<FormInstance>()

watch(
  () => props.domains,
  (domains) => {
    form.domains = domains.map((domain) => ({ ...domain }))
  },
  { immediate: true, deep: true },
)

const { loading, error, updateAdvertiser } = useUpdateAdvertiser(computed(() => props.advertiserId))

const domainRules: FormItemRule[] = [
  {
    validator: (_rule, value, callback) => {
      if (!formatDomain(String(value ?? '')).trim()) {
        return callback(new Error('Please enter a valid domain'))
      }
      return callback()
    },
    trigger: 'blur',
  },
]

const handleDelete = (domain: Domain) => {
  form.domains = form.domains.filter((item) => item.id !== domain.id)
}

const handleAdd = () => {
  const lastId = form.domains.length ? form.domains[form.domains.length - 1].id : -1
  form.domains = [...form.domains, { id: lastId + 1, text: '' }]
}

const save = async () => {
  if (!formRef.value) {
    return
  }
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }
  const normalizedDomains = form.domains.map((domain) => formatDomain(domain.text).trim())
  await updateAdvertiser({ domains: normalizedDomains })
  if (!error.value) {
    ElNotification.success('Domains saved successfully')
  }
}
</script>

<template>
  <Card title="Domain" title-size="small" class="domain-settings">
    <NoteComponent> The domains are used to track which links should be tied to your store. </NoteComponent>

    <ElForm ref="formRef" :model="form" class="domain-settings__list">
      <FlexComponent
        v-for="(domain, index) in form.domains"
        :key="domain.id"
        direction="row"
        align="center"
        :gap="4"
        class="domain-settings__item"
      >
        <ElFormItem :prop="`domains.${index}.text`" :rules="domainRules" class="domain-settings__form-item">
          <InputComponent v-model="form.domains[index].text" placeholder="google.se" class="domain-settings__input" />
        </ElFormItem>
        <ButtonComponent
          v-if="form.domains.length > 1"
          is-icon
          variant="transparent"
          class="domain-settings__delete-button"
          aria-label="Delete domain"
          @click="handleDelete(domain)"
        >
          <DeleteIcon />
        </ButtonComponent>
      </FlexComponent>
    </ElForm>

    <ButtonComponent variant="text" class="domain-settings__button domain-settings__add-button" @click="handleAdd">
      <template #iconLeft>
        <ElIcon :size="18">
          <AddPlusIcon />
        </ElIcon>
      </template>
      Add domain
    </ButtonComponent>

    <template #footer>
      <ButtonComponent variant="accent" :is-loading="loading" class="domain-settings__button" @click="save">Save</ButtonComponent>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.domain-settings {
  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__input {
    flex: 1;
  }

  &__form-item {
    flex: 1;
    margin-bottom: 0;
  }

  &__button {
    align-self: flex-start;
  }

  &__delete-button {
    &:hover {
      background: var(--button-hover);
    }
  }
}

.button.domain-settings__add-button {
  padding: 8px 16px 8px 12px;
}
</style>
